import { getContext, getElement, store } from '@wordpress/interactivity'
import type { Post, PostsResponse } from './types'

type Context = {
  posts: Post[]
  currentPage: number
  totalPages: number | null
  displayPagesLimit: number
  category: number
}

declare global {
  interface Window {
    YT_CONFIG: {
      restUrl: string
      algoliaProxyHost?: string
      siteSlug?: string
    }
  }
}

function createPageNumbers(from: number, to: number) {
  return Array.from({ length: to - from + 1 }, (_, i) => from + i)
}

async function fetchPostsForPage(page: number, category: number | null) {
  const response = await fetch(
    `${window.YT_CONFIG.restUrl}yt/v1/blocks/yellowtree/post-list/posts?page=${page}&category=${category}`
  )
  return (await response.json()) as PostsResponse
}

const { state, callbacks } = store('yellowtreePostList', {
  state: {
    get isLoading() {
      return state.pendingPageNumbers.length > 0
    },
    hasError: false,
    get showMoreButton() {
      const context = getContext<Context>()
      return (
        !this.isLoading && context.displayPagesLimit < (context.totalPages ?? 0)
      )
    },
    get postList() {
      const context = getContext<Context>()
      return context.posts.filter((p) => {
        return p.page >= context.currentPage
      })
    },
    pendingPageNumbers: [] as number[],
    get pageNumbers() {
      const context = getContext<Context>()
      return createPageNumbers(1, context.totalPages ?? 1)
    },
    get pageNumber() {
      const context = getContext<Context & { number: number }>()
      const baseUrl = window.location.pathname
      const number = context.number
      return `${baseUrl}?from=${number}`
    },
    get showNoResults() {
      const context = getContext<Context>()
      return !this.isLoading && !this.hasError && context.posts.length < 1
    },
    get showPagination() {
      return this.pageNumbers.length > 1
    },
    get isCategoryActive() {
      const element = getElement()
      const context = getContext<Context>()
      return context.category === Number.parseInt(element.attributes.value)
    }
  },
  actions: {
    onLoadMore: () => {
      const context = getContext<Context>()
      if (
        context.totalPages !== null &&
        context.displayPagesLimit >= context.totalPages
      ) {
        return
      }
      context.displayPagesLimit += 1
      callbacks.updatePosts()
    },
    changeCategory: (event: Event) => {
      const target = event.target as HTMLSelectElement
      const context = getContext<Context>()
      context.category = Number.parseInt(target.value)
      context.posts = []
      context.currentPage = 1
      context.totalPages = null
      context.displayPagesLimit = 1
      callbacks.updatePosts()
    }
  },
  callbacks: {
    fetchPostsForPages: async (pages: number[], category: number | null) => {
      const context = getContext<Context>()

      try {
        state.hasError = false

        const todoPageNumbers: number[] = pages.filter(
          (page) => context.posts.find((p) => p.page === page) === undefined
        )

        state.pendingPageNumbers = [...todoPageNumbers]

        for (const page of todoPageNumbers) {
          const data = await fetchPostsForPage(page, category)
          context.posts = [...context.posts, ...data.posts]
          context.totalPages = data.totalPages

          state.pendingPageNumbers = state.pendingPageNumbers.filter(
            (p) => p !== page
          )
        }
      } catch (error) {
        console.error('Error fetching posts:', error)
        state.hasError = true
      }
    },
    updatePosts: () => {
      const context = getContext<Context>()
      callbacks.fetchPostsForPages(
        createPageNumbers(context.currentPage, context.displayPagesLimit),
        context.category
      )
    },
    updateQueryParams: () => {
      const context = getContext<Context & { number: number }>()
      const baseUrl = window.location.pathname
      const queryParams = new URLSearchParams()
      if (context.category !== -1) {
        queryParams.set('category', context.category.toString())
      } else {
        queryParams.delete('category')
      }
      let url = `${baseUrl}?${queryParams.toString()}`
      if (queryParams.toString() === '') {
        url = baseUrl
      }
      window.history.pushState({}, '', url)
    },
    historyChange: () => {
      const context = getContext<Context>()
      const params = new URLSearchParams(window.location.search)
      const categoryParam = params.get('category')

      if (categoryParam) {
        context.category = Number.parseInt(categoryParam)
      } else {
        context.category = -1
      }
      context.posts = []
      callbacks.updatePosts()
    }
  }
})
